import { Locale } from '@/providers/Locale/types'

export type Slug = (typeof i18n)[Locale][KnownCategories]['slug']

export type KnownCategories =
    | 'businessNews'
    | 'lifestyle'
    | 'money'
    | 'innovation'
    | 'leadership'
    | 'ai'
    | 'autos'
    | 'airlines'
    | 'retail'
    | 'food'
    | 'pharma'
    | 'politics'
    | 'finance'
    | 'economics'
    | 'earnings'
    | 'markets'
    | 'investing'
    | 'collecting'
    | 'cars'
    | 'healthFitness'
    | 'foodFineDining'
    | 'travel'
    | 'realEstate'
    | 'futureOfWork'
    | 'news'
    | 'emergingTechnologies'
    | 'cloudComputing'
    | 'space'
    | 'cyberSecurity'
    | 'dailyBrief'
    | 'email'
    | 'series'
    | 'quartzObsessionPodcast'
    | 'workplace'
    | 'airAndSpace'
    | 'chiefInnovationOfficerForecast'

interface CategoryMeta {
    heading: string
    description: string
    slug: string
    url: string
}

export function isKnownCategoryArray(locale: Locale, categories: Array<string> | Array<KnownCategories>): categories is Array<KnownCategories> {
    try {
        return (categories[0] || 'unknown') in i18n[locale]
    } catch (e) {
        console.error('Unable to validate categories for locale:', locale, e)
    }

    return false
}

export const i18n = {
    en: {
        lifestyle: {
            heading: 'Lifestyle',
            description: '',
            url: '/lifestyle',
            slug: 'lifestyle',
        },
        money: {
            heading: 'Money & Markets',
            description: 'Understand the forces shaping the economy—and your portfolio.',
            url: '/money-markets',
            slug: 'money-markets',
        },
        innovation: {
            heading: 'Tech & Innovation',
            description: 'The ideas, executives, and technologies accelerating change in business and society.',
            url: '/tech-innovation',
            slug: 'tech-innovation',
        },
        businessNews: {
            heading: 'Business News',
            description: 'In-depth coverage and articles from Quartz about Business News.',
            url: '/business-news',
            slug: 'business-news',
        },
        leadership: {
            heading: 'Leadership',
            description: '',
            url: '/leadership',
            slug: 'leadership',
        },
        ai: {
            heading: 'Generation A.I.',
            description: 'Generating the future of innovation and intelligence.',
            url: '/tech-innovation/ai',
            slug: 'ai',
        },
        autos: {
            heading: 'Autos',
            description: 'Driving innovation and industry trends.',
            url: '/business-news/autos',
            slug: 'autos',
        },
        airlines: { heading: 'Airlines', description: 'Navigating the business of global travel.', url: '/business-news/airlines', slug: 'airlines' },
        retail: { heading: 'Retail', description: 'Redefining commerce in a changing world.', url: '/business-news/retail', slug: 'retail' },
        food: { heading: 'Food', description: 'The business behind what we eat.', url: '/business-news/food', slug: 'food' },
        pharma: { heading: 'Pharma', description: 'Transforming health through business and innovation.', url: '/business-news/pharma', slug: 'pharma' },
        politics: {
            heading: 'Politics & Government',
            description: 'Shaping policy, power and business impact.',
            url: '/business-news/politics-government',
            slug: 'politics-government',
        },
        finance: {
            heading: 'Personal Finance',
            description: 'Saving, budgeting, and financial planning.',
            url: '/money-markets/personal-finance',
            slug: 'personal-finance',
        },
        economics: {
            heading: 'Economic Indicators',
            description: 'Reports on inflation, interest rates, and GDP.',
            url: '/money-markets/economic-indicators',
            slug: 'economic-indicators',
        },
        earnings: {
            heading: 'Earnings Snapshots',
            description: 'Quick insights on U.S. public company performance.',
            url: '/money-markets/earnings-snapshots',
            slug: 'earnings-snapshots',
        },
        markets: { heading: 'Markets', description: 'Tracking trends and movements in the U.S. economy.', url: '/money-markets/markets', slug: 'markets' },
        investing: {
            heading: 'Smart Investing',
            description: 'Weekly advice from investment professionals.',
            url: '/money-markets/smart-investing',
            slug: 'smart-investing',
        },
        collecting: { heading: 'Collecting', url: '/lifestyle/collecting', description: 'The art and stories behind what we treasure.', slug: 'collecting' },
        cars: { heading: 'Cars', url: '/lifestyle/cars', description: 'Design, performance, and the joy of the road.', slug: 'cars' },
        healthFitness: {
            heading: 'Health & Fitness',
            url: '/lifestyle/health-fitness',
            description: 'Strength and wellness for modern living.',
            slug: 'health-fitness',
        },
        foodFineDining: {
            heading: 'Food & Fine Dining',
            url: '/lifestyle/food-fine-dining',
            description: 'Global cuisine, fine dining trends, and culinary innovations.',
            slug: 'food-fine-dining',
        },
        travel: { heading: 'Travel', url: '/lifestyle/travel', description: 'Destinations, experiences, and industry trends.', slug: 'travel' },
        realEstate: { heading: 'Real Estate', url: '/lifestyle/real-estate', description: 'Living spaces and investment opportunities.', slug: 'real-estate' },
        futureOfWork: {
            heading: 'Future of Work',
            url: '/leadership/future-of-work',
            description: 'Adapting to changes in workplace dynamics and technology.',
            slug: 'future-of-work',
        },
        news: { heading: 'News', description: 'Updates from major players like Apple, Google, and Tesla.', url: '/tech-innovation/news', slug: 'news' },
        emergingTechnologies: {
            heading: 'Emerging Technologies',
            description: 'Emerging blockchain, quantum computing, and robotics.',
            url: '/tech-innovation/emerging-technologies',
            slug: 'emerging-technologies',
        },
        cloudComputing: {
            heading: 'Cloud Computing',
            description: 'Driving the future of digital infrastructure.',
            url: '/tech-innovation/cloud-computing',
            slug: 'cloud-computing',
        },
        space: { heading: 'Space', description: 'Advancing the business of the beyond.', url: '/tech-innovation/space', slug: 'space' },
        cyberSecurity: {
            heading: 'Cybersecurity',
            description: 'Safeguarding a connected world.',
            url: '/tech-innovation/cybersecurity',
            slug: 'cybersecurity',
        },
        dailyBrief: {
            heading: 'Quartz Daily Brief',
            description: 'Sign up for the Daily Brief',
            url: '/emails/daily-brief',
            slug: 'daily-brief',
        },
        email: {
            heading: 'Emails',
            description: 'In-depth coverage and articles from Quartz about Emails.',
            url: '/emails',
            slug: 'emails',
        },
        series: {
            heading: 'Series',
            description: '',
            url: '/series',
            slug: 'series',
        },
        quartzObsessionPodcast: {
            heading: 'Quartz Obsession',
            description:
                'Join us every week as reporters from our global newsroom dig into the most fascinating facets of an idea: where it came from, how it got to us, and what it can tell us about the forces that are changing the way we live and work.',
            url: '/series/quartz-obsession-podcast',
            slug: 'quartz-obsession-podcast',
        },
        workplace: {
            heading: 'Workplace',
            description: 'In-depth coverage and articles from Quartz about Leadership - Workplace.',
            url: '/leadership/workplace',
            slug: 'workplace',
        },
        airAndSpace: {
            heading: 'Air and Space',
            description:
                'With aerospace journalists from Quartz, this series explores the new frontiers of science, technology, and business on Earth and beyond.',
            url: '/business-news/air-and-space',
            slug: 'air-and-space',
        },
        chiefInnovationOfficerForecast: {
            heading: 'Chief Innovation Officer Forecast',
            description: "Shaping the strategies for tomorrow's tech.",
            url: '/tech-innovation/chief-innovation-officer-forecast',
            slug: 'chief-innovation-officer-forecast',
        },
    },
    es: {
        lifestyle: {
            heading: 'Estilo de vida',
            description: 'Cobertura detallada y artículos sobre Estilo de Vida de Quartz.',
            url: '/estilo-de-vida',
            slug: 'estilo-de-vida',
        },
        money: {
            heading: 'Dinero y Mercados',
            description: 'Cobertura detallada y artículos sobre Dinero y Mercados de Quartz.',
            url: '/dinero-y-mercados',
            slug: 'dinero-y-mercados',
        },
        innovation: {
            heading: 'Tecnología y Innovación',
            description: 'Cobertura detallada y artículos sobre Tecnología y Innovación de Quartz.',
            url: '/tecnologia-y-innovacion',
            slug: 'tecnologia-y-innovacion',
        },
        businessNews: {
            heading: 'Noticias de Negocios',
            description: 'Cobertura detallada y artículos sobre Noticias de Negocios de Quartz.',
            url: '/noticias-de-negocios',
            slug: 'noticias-de-negocios',
        },
        leadership: {
            heading: 'Liderazgo',
            description: 'Cobertura detallada y artículos sobre Liderazgo de Quartz.',
            url: '/liderazgo',
            slug: 'liderazgo',
        },
        ai: {
            heading: 'Generación A.I.',
            description: 'Generando el futuro de la innovación y la inteligencia.',
            url: '/tech-innovation/ai',
            slug: 'tecnologia-y-innovacion',
        },
        autos: {
            heading: 'Autos',
            description: 'Impulsando la innovación y las tendencias de la industria.',
            url: '/business-news/autos',
            slug: 'noticias-de-negocios',
        },
        airlines: {
            heading: 'Aerolíneas',
            description: 'Navegando el negocio de los viajes globales.',
            url: '/business-news/airlines',
            slug: 'noticias-de-negocios',
        },
        retail: {
            heading: 'Comercio Minorista',
            description: 'Redefiniendo el comercio en un mundo cambiante.',
            url: '/business-news/retail',
            slug: 'noticias-de-negocios',
        },
        food: {
            heading: 'Comida',
            description: 'El negocio detrás de lo que comemos.',
            url: '/business-news/food',
            slug: 'estilo-de-vida',
        },
        pharma: {
            heading: 'Farmacéutica',
            description: 'Transformando la salud a través de los negocios y la innovación.',
            url: '/business-news/pharma',
            slug: 'noticias-de-negocios',
        },
        politics: {
            heading: 'Política y Gobierno',
            description: 'Dando forma a la política, el poder y el impacto en los negocios.',
            url: '/business-news/politics-government',
            slug: 'noticias-de-negocios',
        },
        finance: {
            heading: 'Finanzas Personales',
            description: 'Ahorro, presupuesto y planificación financiera.',
            url: '/money-markets/personal-finance',
            slug: 'dinero-y-mercados',
        },
        economics: {
            heading: 'Indicadores Económicos',
            description: 'Informes sobre inflación, tasas de interés y PIB.',
            url: '/money-markets/economic-indicators',
            slug: 'dinero-y-mercados',
        },
        earnings: {
            heading: 'Ganancias',
            description: 'Información rápida sobre el rendimiento de empresas públicas en EE.UU.',
            url: '/money-markets/earnings-snapshots',
            slug: 'dinero-y-mercados',
        },
        markets: {
            heading: 'Mercados',
            description: 'Seguimiento de tendencias y movimientos en la economía de EE.UU.',
            url: '/money-markets/markets',
            slug: 'dinero-y-mercados',
        },
        investing: {
            heading: 'Inversión Inteligente',
            description: 'Consejos semanales de profesionales de la inversión.',
            url: '/money-markets/smart-investing',
            slug: 'dinero-y-mercados',
        },
        collecting: {
            heading: 'Coleccionismo',
            description: 'El arte y las historias detrás de lo que atesoramos.',
            url: '/lifestyle/collecting',
            slug: 'estilo-de-vida',
        },
        cars: {
            heading: 'Autos',
            description: 'Diseño, rendimiento y el placer de la carretera.',
            url: '/lifestyle/cars',
            slug: 'estilo-de-vida',
        },
        healthFitness: {
            heading: 'Salud y Bienestar',
            description: 'Fuerza y bienestar para la vida moderna.',
            url: '/lifestyle/health-fitness',
            slug: 'estilo-de-vida',
        },
        foodFineDining: {
            heading: 'Comida y Alta Cocina',
            description: 'Cocina global, tendencias de alta cocina e innovaciones culinarias.',
            url: '/lifestyle/food-fine-dining',
            slug: 'estilo-de-vida',
        },
        travel: {
            heading: 'Viajes',
            description: 'Destinos, experiencias y tendencias de la industria.',
            url: '/lifestyle/travel',
            slug: 'estilo-de-vida',
        },
        realEstate: {
            heading: 'Bienes Raíces',
            description: 'Espacios habitables y oportunidades de inversión.',
            url: '/lifestyle/real-estate',
            slug: 'estilo-de-vida',
        },
        futureOfWork: {
            heading: 'Futuro del Trabajo',
            description: 'Adaptándose a los cambios en la dinámica laboral y la tecnología.',
            url: '/leadership/future-of-work',
            slug: 'liderazgo',
        },
        news: {
            heading: 'Noticias',
            description: 'Actualizaciones de jugadores importantes como Apple, Google y Tesla.',
            url: '/tech-innovation/news',
            slug: 'tecnologia-y-innovacion',
        },
        emergingTechnologies: {
            heading: 'Tecnologías Emergentes',
            description: 'Blockchain, computación cuántica y robótica emergentes.',
            url: '/tech-innovation/emerging-technologies',
            slug: 'tecnologia-y-innovacion',
        },
        cloudComputing: {
            heading: 'Computación en la Nube',
            description: 'Impulsando el futuro de la infraestructura digital.',
            url: '/tech-innovation/cloud-computing',
            slug: 'tecnologia-y-innovacion',
        },
        space: {
            heading: 'Espacio',
            description: 'Avanzando en el negocio del más allá.',
            url: '/tech-innovation/space',
            slug: 'tecnologia-y-innovacion',
        },
        cyberSecurity: {
            heading: 'Ciberseguridad',
            description: 'Protegiendo un mundo conectado.',
            url: '/tech-innovation/cybersecurity',
            slug: 'tecnologia-y-innovacion',
        },
        dailyBrief: {
            heading: 'Resumen Diario de Quartz',
            description: 'Regístrate para el Resumen Diario',
            url: '/correos',
            slug: 'correos',
        },
        email: {
            heading: 'Correos',
            description: 'Cobertura detallada y artículos sobre Correos de Quartz.',
            url: '/correos',
            slug: 'correos',
        },
        series: {
            heading: 'Series',
            description: '',
            url: '/series',
            slug: 'series',
        },
        quartzObsessionPodcast: {
            heading: 'Quartz Obsession',
            description:
                'Únase a nosotros cada semana mientras los reporteros de nuestra sala de redacción global profundizan en las facetas más fascinantes de una idea: de dónde surgió, cómo llegó a nosotros y qué puede decirnos sobre las fuerzas que están cambiando la forma en que vivimos y trabajamos.',
            url: '/series/quartz-obsession-podcast',
            slug: 'quartz-obsession-podcast',
        },
        workplace: {
            heading: 'Lugar de trabajo',
            description: 'Cobertura detallada y artículos sobre Liderazgo - Lugar de trabajo de Quartz.',
            url: '', // 404
            slug: '', // 404
        },
        airAndSpace: {
            heading: 'Aire y Espacio',
            description:
                'Con periodistas aeroespaciales de Quartz, esta serie explora las nuevas fronteras de la ciencia, la tecnología y los negocios en la Tierra y más allá.',
            url: '', // 404
            slug: '', // 404
        },
        chiefInnovationOfficerForecast: {
            heading: 'Pronóstico del Director de Innovación',
            description: 'Dando forma a las estrategias para la tecnología del mañana.',
            url: '',
            slug: '',
        },
    },
    fr: {
        lifestyle: {
            heading: 'Lifestyle',
            description: 'Couverture approfondie et articles de Quartz sur Lifestyle.',
            url: '/lifestyle',
            slug: 'lifestyle',
        },
        money: {
            heading: 'Finances et Affaires',
            description: 'Couverture approfondie et articles de Quartz sur Finances et Affaires.',
            url: '/finances-et-affaires',
            slug: 'finances-et-affaires',
        },
        innovation: {
            heading: 'Technologie et Innovation',
            description: 'Couverture approfondie et articles de Quartz sur Tech et Innovation.',
            url: '/tech-et-innovation',
            slug: 'tech-et-innovation',
        },
        businessNews: {
            heading: 'Actualité Business',
            description: 'Couverture approfondie et articles de Quartz sur Actualité Business.',
            url: '/actualite-business',
            slug: 'actualite-business',
        },
        leadership: {
            heading: 'Leadership',
            description: '',
            url: '/leadership',
            slug: 'leadership',
        },
        ai: {
            heading: 'Génération A.I.',
            description: "Générer l'avenir de l'innovation et de l'intelligence.",
            url: '/tech-innovation/ai',
            slug: 'tech-et-innovation',
        },
        autos: {
            heading: 'Automobiles',
            description: "Stimuler l'innovation et les tendances de l'industrie.",
            url: '/business-news/autos',
            slug: 'actualite-business',
        },
        airlines: {
            heading: 'Compagnies aériennes',
            description: 'Explorer les affaires du voyage mondial.',
            url: '/business-news/airlines',
            slug: 'actualite-business',
        },
        retail: {
            heading: 'Commerce de détail',
            description: 'Redéfinir le commerce dans un monde en mutation.',
            url: '/business-news/retail',
            slug: 'actualite-business',
        },
        food: {
            heading: 'Alimentation',
            description: 'Les affaires derrière ce que nous mangeons.',
            url: '/business-news/food',
            slug: 'lifestyle',
        },
        pharma: {
            heading: 'Pharmaceutique',
            description: "Transformer la santé grâce aux affaires et à l'innovation.",
            url: '/business-news/pharma',
            slug: 'actualite-business',
        },
        politics: {
            heading: 'Politique et Gouvernement',
            description: "Façonner les politiques, le pouvoir et l'impact économique.",
            url: '/business-news/politics-government',
            slug: 'actualite-business',
        },
        finance: {
            heading: 'Finances personnelles',
            description: 'Épargner, budgéter et planifier ses finances.',
            url: '/money-markets/personal-finance',
            slug: 'finances-et-affaires',
        },
        economics: {
            heading: 'Indicateurs économiques',
            description: "Rapports sur l'inflation, les taux d'intérêt et le PIB.",
            url: '/money-markets/economic-indicators',
            slug: 'finances-et-affaires',
        },
        earnings: {
            heading: 'Résultats financiers',
            description: 'Aperçus rapides sur les performances des entreprises cotées aux États-Unis.',
            url: '/money-markets/earnings-snapshots',
            slug: 'finances-et-affaires',
        },
        markets: {
            heading: 'Marchés',
            description: "Suivi des tendances et des mouvements de l'économie américaine.",
            url: '/money-markets/markets',
            slug: 'finances-et-affaires',
        },
        investing: {
            heading: 'Investissement intelligent',
            description: "Conseils hebdomadaires de professionnels de l'investissement.",
            url: '/money-markets/smart-investing',
            slug: 'finances-et-affaires',
        },
        collecting: {
            heading: 'Collection',
            description: "L'art et les histoires derrière ce que nous chérissons.",
            url: '/lifestyle/collecting',
            slug: 'lifestyle',
        },
        cars: {
            heading: 'Voitures',
            description: 'Design, performance et plaisir de conduire.',
            url: '/lifestyle/cars',
            slug: 'lifestyle',
        },
        healthFitness: {
            heading: 'Santé et forme',
            description: 'Force et bien-être pour la vie moderne.',
            url: '/lifestyle/health-fitness',
            slug: 'lifestyle',
        },
        foodFineDining: {
            heading: 'Gastronomie',
            description: 'Cuisine mondiale, tendances gastronomiques et innovations culinaires.',
            url: '/lifestyle/food-fine-dining',
            slug: 'lifestyle',
        },
        travel: {
            heading: 'Voyages',
            description: 'Destinations, expériences et tendances du secteur.',
            url: '/lifestyle/travel',
            slug: 'lifestyle',
        },
        realEstate: {
            heading: 'Immobilier',
            description: "Espaces de vie et opportunités d'investissement.",
            url: '/lifestyle/real-estate',
            slug: 'lifestyle',
        },
        futureOfWork: {
            heading: 'Futur du travail',
            description: "S'adapter aux évolutions du travail et de la technologie.",
            url: '/leadership/future-of-work',
            slug: 'leadership',
        },
        news: {
            heading: 'Actualités',
            description: 'Mises à jour des acteurs majeurs comme Apple, Google et Tesla.',
            url: '/tech-innovation/news',
            slug: 'tech-et-innovation',
        },
        emergingTechnologies: {
            heading: 'Technologies émergentes',
            description: 'Blockchain, informatique quantique et robotique émergentes.',
            url: '/tech-innovation/emerging-technologies',
            slug: 'tech-et-innovation',
        },
        cloudComputing: {
            heading: 'Informatique en nuage',
            description: "Conduire l'avenir de l'infrastructure numérique.",
            url: '/tech-innovation/cloud-computing',
            slug: 'tech-et-innovation',
        },
        space: {
            heading: 'Espace',
            description: 'Faire avancer les affaires au-delà de la Terre.',
            url: '/tech-innovation/space',
            slug: 'tech-et-innovation',
        },
        cyberSecurity: {
            heading: 'Cybersécurité',
            description: 'Protéger un monde connecté.',
            url: '/tech-innovation/cybersecurity',
            slug: 'tech-et-innovation',
        },
        dailyBrief: {
            heading: 'Brief quotidien de Quartz',
            description: 'Inscrivez-vous au Brief quotidien',
            url: '/e-mails',
            slug: 'e-mails',
        },
        email: {
            heading: 'E-mails',
            description: 'Couverture approfondie et articles de Quartz sur E-mails.',
            url: '/e-mails',
            slug: 'e-mails',
        },
        series: {
            heading: 'Series',
            description: '',
            url: '/series',
            slug: 'series',
        },
        quartzObsessionPodcast: {
            heading: 'Quartz Obsession',
            description:
                "Rejoignez-nous chaque semaine alors que les journalistes de notre salle de presse mondiale explorent les facettes les plus fascinantes d'une idée : d'où elle vient, comment elle nous est parvenue et ce qu'elle peut nous dire sur les forces qui changent notre façon de vivre et de travailler.",
            url: '/series/quartz-obsession-podcast',
            slug: 'quartz-obsession-podcast',
        },
        workplace: {
            heading: 'Lieu de travail',
            description: 'Couverture approfondie et articles de Quartz sur Leadership - Lieu de travail.',
            url: '', // 404
            slug: '', // 404
        },
        airAndSpace: {
            heading: 'Air et Espace',
            description:
                'Avec des journalistes aérospatiaux de Quartz, cette série explore les nouvelles frontières de la science, de la technologie et des affaires sur Terre et au-delà.',
            url: '', // 404
            slug: '', // 404
        },
        chiefInnovationOfficerForecast: {
            heading: "Prévisions du directeur de l'innovation",
            description: 'Façonner les stratégies pour la technologie de demain.',
            url: '',
            slug: '',
        },
    },
    de: {
        lifestyle: {
            heading: 'Lifestyle',
            description: 'Ausführliche Berichterstattung und Artikel von Quartz über Lifestyle.',
            url: '/lifestyle',
            slug: 'lifestyle',
        },
        money: {
            heading: 'Geld & Märkte',
            description: 'Ausführliche Berichterstattung und Artikel von Quartz über Geld & Märkte.',
            url: '/geld-markte',
            slug: 'geld-markte',
        },
        innovation: {
            heading: 'Technik & Innovation',
            description: 'Ausführliche Berichterstattung und Artikel von Quartz über Technik & Innovation.',
            url: '/technik-innovation',
            slug: 'technik-innovation',
        },
        businessNews: {
            heading: 'Wirtschaftsnachrichten',
            description: 'Ausführliche Berichterstattung und Artikel von Quartz über Wirtschaftsnachrichten.',
            url: '/wirtschaftsnachrichten',
            slug: 'wirtschaftsnachrichten',
        },
        leadership: {
            heading: 'Führung',
            description: 'Ausführliche Berichterstattung und Artikel von Quartz über Führung.',
            url: '/fuhrung',
            slug: 'fuhrung',
        },
        ai: {
            heading: 'Generation A.I.',
            description: 'Die Zukunft von Innovation und Intelligenz gestalten.',
            url: '/tech-innovation/ai',
            slug: 'technik-innovation',
        },
        autos: {
            heading: 'Autos',
            description: 'Innovation und Branchentrends vorantreiben.',
            url: '/business-news/autos',
            slug: 'wirtschaftsnachrichten',
        },
        airlines: {
            heading: 'Fluggesellschaften',
            description: 'Das Geschäft des globalen Reisens verstehen.',
            url: '/business-news/airlines',
            slug: 'wirtschaftsnachrichten',
        },
        retail: {
            heading: 'Einzelhandel',
            description: 'Handel neu definieren in einer sich wandelnden Welt.',
            url: '/business-news/retail',
            slug: 'wirtschaftsnachrichten',
        },
        food: {
            heading: 'Essen',
            description: 'Das Geschäft hinter dem, was wir essen.',
            url: '/business-news/food',
            slug: 'lifestyle',
        },
        pharma: {
            heading: 'Pharma',
            description: 'Gesundheit durch Geschäft und Innovation transformieren.',
            url: '/business-news/pharma',
            slug: 'wirtschaftsnachrichten',
        },
        politics: {
            heading: 'Politik & Regierung',
            description: 'Politik, Macht und geschäftliche Auswirkungen gestalten.',
            url: '/business-news/politics-government',
            slug: 'wirtschaftsnachrichten',
        },
        finance: {
            heading: 'Private Finanzen',
            description: 'Sparen, Budgetieren und Finanzplanung.',
            url: '/money-markets/personal-finance',
            slug: 'geld-markte',
        },
        economics: {
            heading: 'Wirtschaftsindikatoren',
            description: 'Berichte über Inflation, Zinssätze und BIP.',
            url: '/money-markets/economic-indicators',
            slug: 'geld-markte',
        },
        earnings: {
            heading: 'Gewinne',
            description: 'Schnelle Einblicke in die Leistung börsennotierter US-Unternehmen.',
            url: '/money-markets/earnings-snapshots',
            slug: 'geld-markte',
        },
        markets: {
            heading: 'Märkte',
            description: 'Verfolgung von Trends und Bewegungen in der US-Wirtschaft.',
            url: '/money-markets/markets',
            slug: 'geld-markte',
        },
        investing: {
            heading: 'Intelligentes Investieren',
            description: 'Wöchentliche Tipps von Investmentprofis.',
            url: '/money-markets/smart-investing',
            slug: 'geld-markte',
        },
        collecting: {
            heading: 'Sammeln',
            description: 'Die Kunst und Geschichten hinter dem, was wir schätzen.',
            url: '/lifestyle/collecting',
            slug: 'lifestyle',
        },
        cars: {
            heading: 'Autos',
            description: 'Design, Leistung und die Freude am Fahren.',
            url: '/lifestyle/cars',
            slug: 'lifestyle',
        },
        healthFitness: {
            heading: 'Gesundheit & Fitness',
            description: 'Stärke und Wohlbefinden für das moderne Leben.',
            url: '/lifestyle/health-fitness',
            slug: 'lifestyle',
        },
        foodFineDining: {
            heading: 'Essen & Feinschmeckerküche',
            description: 'Internationale Küche, Gourmettrends und kulinarische Innovationen.',
            url: '/lifestyle/food-fine-dining',
            slug: 'lifestyle',
        },
        travel: {
            heading: 'Reisen',
            description: 'Reiseziele, Erlebnisse und Branchentrends.',
            url: '/lifestyle/travel',
            slug: 'lifestyle',
        },
        realEstate: {
            heading: 'Immobilien',
            description: 'Wohnräume und Investitionsmöglichkeiten.',
            url: '/lifestyle/real-estate',
            slug: 'lifestyle',
        },
        futureOfWork: {
            heading: 'Zukunft der Arbeit',
            description: 'Anpassung an Veränderungen in Arbeitswelt und Technologie.',
            url: '/leadership/future-of-work',
            slug: 'fuhrung',
        },
        news: {
            heading: 'Nachrichten',
            description: 'Neuigkeiten von großen Playern wie Apple, Google und Tesla.',
            url: '/tech-innovation/news',
            slug: 'technik-innovation',
        },
        emergingTechnologies: {
            heading: 'Zukunftstechnologien',
            description: 'Neue Entwicklungen in Blockchain, Quantencomputing und Robotik.',
            url: '/tech-innovation/emerging-technologies',
            slug: 'technik-innovation',
        },
        cloudComputing: {
            heading: 'Cloud Computing',
            description: 'Die Zukunft digitaler Infrastruktur gestalten.',
            url: '/tech-innovation/cloud-computing',
            slug: 'technik-innovation',
        },
        space: {
            heading: 'Weltraum',
            description: 'Vorantreiben der Geschäfte jenseits der Erde.',
            url: '/tech-innovation/space',
            slug: 'technik-innovation',
        },
        cyberSecurity: {
            heading: 'Cybersicherheit',
            description: 'Ein vernetztes System schützen.',
            url: '/tech-innovation/cybersecurity',
            slug: 'technik-innovation',
        },
        dailyBrief: {
            heading: 'Quartz Tagesüberblick',
            description: 'Für den Tagesüberblick anmelden',
            url: '/e-mails',
            slug: 'e-mails',
        },
        email: {
            heading: 'E-mails',
            description: 'Ausführliche Berichterstattung und Artikel von Quartz über E-Mails.',
            url: '/e-mails',
            slug: 'e-mails',
        },
        series: {
            heading: 'Series',
            description: '',
            url: '/series',
            slug: 'series',
        },
        quartzObsessionPodcast: {
            heading: 'Quartz Obsession',
            description:
                'Seien Sie jede Woche dabei, wenn Reporter unserer globalen Nachrichtenredaktion die faszinierendsten Facetten einer Idee untersuchen: Woher sie stammt, wie sie zu uns gelangt ist und was sie uns über die Kräfte sagen kann, die unsere Lebens- und Arbeitsweise verändern.',
            url: '/series/quartz-obsession-podcast',
            slug: 'quartz-obsession-podcast',
        },
        workplace: {
            heading: 'Arbeitsplatz',
            description: 'Ausführliche Berichterstattung und Artikel von Quartz über Führung - Arbeitsplatz.',
            url: '', // 404
            slug: '', // 404
        },
        airAndSpace: {
            heading: 'Luft und Raumfahrt',
            description:
                'Mit Journalisten von Quartz, die sich auf Luft und Raumfahrt spezialisiert haben, untersucht diese Serie die neuen Grenzen von Wissenschaft, Technologie und Wirtschaft auf der Erde und darüber hinaus.',
            url: '', // 404
            slug: '', // 404
        },
        chiefInnovationOfficerForecast: {
            heading: 'Chief Innovation Officer Forecast',
            description: 'Strategien für die Technologie von morgen gestalten.',
            url: '',
            slug: '',
        },
    },
} as const satisfies Record<Locale, Record<KnownCategories, CategoryMeta>>
